import { DATATEST } from "constants/integration";
import { CollectionAction, View } from "constants/product-collection";
import { merge } from "glamor";
import { ICollectionVersionStatus } from "helpers/collection-version-status";
import { getMenuItems } from "helpers/utils/collection-actions";
import IconButton from "material-ui/IconButton";
import Menu from "material-ui/IconMenu";
import MenuItem from "material-ui/MenuItem";
import ChevronRight from "material-ui/svg-icons/navigation/chevron-right";
import MoreVertIcon from "material-ui/svg-icons/navigation/more-vert";
import SyncIcon from "material-ui/svg-icons/notification/sync";
import React, { memo } from "react";
import { ISubscription } from "reducers/subscriptions";
import { IViews } from "../../product-collection-toolbar/constants";

const nestedStyles = merge({
    display: "flex",
    alignItems: "center",
});

interface MenuActionsProps {
    syncingProducts: boolean;
    allocatingAssortment: boolean;
    findingSizes: boolean;
    syncingInventoryUnits: boolean;
    bulkItems: any;
    isMobileOrPhablet: boolean;
    isMobileOrPhaTablet: boolean;
    showTimeline: boolean;
    onToggleTimeline: () => void;
    views: IViews[];
    view: View;
    handleChangeView: (event) => void;
    editable: boolean;
    itemId?: string;
    onToggleAddSMUDialog?: () => void;
    showSaveAsAction: boolean;
    handleOpenSaveAsDialog: () => void;
    onSyncProductsStop?: () => void;
    onSyncProductsStart?: (isFiltered?: boolean) => void;
    sizePackEnabled: boolean;
    handleOpenSizeSettingsDialog: () => void;
    sizingRangeEnabled: boolean;
    handleFindSizeRangesAllRows: () => void;
    sizingCurveEnabled: boolean;
    handleAllocateAllRows: () => void;
    isCloseoutAssortmentEnabled: boolean;
    isBrandUser: boolean;
    handleSyncInventoryUnitsAllRows: (action: CollectionAction) => void;
    versionStatuses?: ICollectionVersionStatus[];
    currentTag?: string;
    handleVersionStatusClicked?: (event: any) => void;
    handleViewHistory?: () => void;
    showExportToMerchant?: boolean;
    merchantName?: string;
    handleOpenExportToMerchantDialog?: () => void;
    enabledExportActions?: boolean;
    handleViewValidationErrorLog?: () => void;
    handleViewExportStatusLog?: () => void;
    canUseXLS: boolean;
    handleOpenExportDialog?: () => void;
    showJWN: boolean;
    showDeleteAction: boolean;
    handleOpenSendToRetailerDialog: () => void;
    subscription: ISubscription;
    handleSubscriptionAction: () => void;
    showAssortmentSettings: boolean;
    onOpenAssortmentSettings: () => void;
    canEdit: boolean;
    collectionId: string;
    handleOpenCopyDialog: () => void;
    handleOpenDeleteDialog: () => void;
    isUpcManagementEnabled: boolean;
    onOpenExportUpcDialog: () => void;
    onOpenImportUpcDialog: () => void;
    showCopyAction: boolean;
}

const MenuActions = ({
    bulkItems,
    isMobileOrPhablet,
    isMobileOrPhaTablet,
    showTimeline,
    onToggleTimeline,
    views,
    view,
    handleChangeView,
    editable,
    itemId,
    onToggleAddSMUDialog,
    showSaveAsAction,
    handleOpenSaveAsDialog,
    syncingProducts,
    onSyncProductsStop,
    onSyncProductsStart,
    sizePackEnabled,
    handleOpenSizeSettingsDialog,
    sizingRangeEnabled,
    findingSizes,
    handleFindSizeRangesAllRows,
    sizingCurveEnabled,
    allocatingAssortment,
    handleAllocateAllRows,
    isCloseoutAssortmentEnabled,
    isBrandUser,
    syncingInventoryUnits,
    handleSyncInventoryUnitsAllRows,
    versionStatuses,
    currentTag,
    handleVersionStatusClicked,
    handleViewHistory,
    showExportToMerchant,
    merchantName,
    handleOpenExportToMerchantDialog,
    enabledExportActions,
    handleViewValidationErrorLog,
    handleViewExportStatusLog,
    canUseXLS,
    handleOpenExportDialog,
    showJWN,
    showDeleteAction,
    handleOpenSendToRetailerDialog,
    subscription,
    handleSubscriptionAction,
    showAssortmentSettings,
    onOpenAssortmentSettings,
    canEdit,
    collectionId,
    handleOpenCopyDialog,
    handleOpenDeleteDialog,
    isUpcManagementEnabled,
    onOpenExportUpcDialog,
    onOpenImportUpcDialog,
    showCopyAction,
}: MenuActionsProps) => {
    const renderKabobOpenerIcon = () => {
        if (syncingProducts || allocatingAssortment || findingSizes || syncingInventoryUnits) {
            return <SyncIcon className="spinningWheel" />;
        }

        return <MoreVertIcon />;
    };

    const menuItems = React.useMemo(
        () =>
            getMenuItems({
                isMobileOrPhablet,
                isMobileOrPhaTablet,
                showTimeline,
                onToggleTimeline,
                views,
                view,
                handleChangeView,
                editable,
                itemId,
                onToggleAddSMUDialog,
                showSaveAsAction,
                handleOpenSaveAsDialog,
                syncingProducts,
                onSyncProductsStop,
                onSyncProductsStart,
                sizePackEnabled,
                handleOpenSizeSettingsDialog,
                sizingRangeEnabled,
                findingSizes,
                handleFindSizeRangesAllRows,
                sizingCurveEnabled,
                allocatingAssortment,
                handleAllocateAllRows,
                isCloseoutAssortmentEnabled,
                isBrandUser,
                syncingInventoryUnits,
                handleSyncInventoryUnitsAllRows,
                versionStatuses,
                currentTag,
                handleVersionStatusClicked,
                handleViewHistory,
                showExportToMerchant,
                merchantName,
                handleOpenExportToMerchantDialog,
                enabledExportActions,
                handleViewValidationErrorLog,
                handleViewExportStatusLog,
                canUseXLS,
                handleOpenExportDialog,
                showJWN,
                showDeleteAction,
                handleOpenSendToRetailerDialog,
                subscription,
                handleSubscriptionAction,
                showAssortmentSettings,
                onOpenAssortmentSettings,
                canImportUpcs: canEdit,
                collectionId,
                handleOpenCopyDialog,
                handleOpenDeleteDialog,
                isUpcManagementEnabled,
                onOpenExportUpcDialog,
                onOpenImportUpcDialog,
                showCopyAction,
            }),
        [
            allocatingAssortment,
            canEdit,
            canUseXLS,
            collectionId,
            currentTag,
            editable,
            enabledExportActions,
            findingSizes,
            handleAllocateAllRows,
            handleChangeView,
            handleFindSizeRangesAllRows,
            handleOpenCopyDialog,
            handleOpenDeleteDialog,
            handleOpenExportDialog,
            handleOpenExportToMerchantDialog,
            handleOpenSaveAsDialog,
            handleOpenSendToRetailerDialog,
            handleOpenSizeSettingsDialog,
            handleSubscriptionAction,
            handleSyncInventoryUnitsAllRows,
            handleVersionStatusClicked,
            handleViewExportStatusLog,
            handleViewHistory,
            handleViewValidationErrorLog,
            isBrandUser,
            isCloseoutAssortmentEnabled,
            isMobileOrPhaTablet,
            isMobileOrPhablet,
            isUpcManagementEnabled,
            itemId,
            merchantName,
            onOpenAssortmentSettings,
            onOpenExportUpcDialog,
            onOpenImportUpcDialog,
            onSyncProductsStart,
            onSyncProductsStop,
            onToggleAddSMUDialog,
            onToggleTimeline,
            showAssortmentSettings,
            showCopyAction,
            showDeleteAction,
            showExportToMerchant,
            showJWN,
            showSaveAsAction,
            showTimeline,
            sizePackEnabled,
            sizingCurveEnabled,
            sizingRangeEnabled,
            subscription,
            syncingInventoryUnits,
            syncingProducts,
            versionStatuses,
            view,
            views,
        ]
    );

    return (
        <Menu
            iconButtonElement={
                <IconButton disabled={bulkItems.size > 0} data-test={DATATEST.ASSORTMENTS.ASSORTMENT_VIEW_ACTION_BTN}>
                    {renderKabobOpenerIcon()}
                </IconButton>
            }
            anchorOrigin={{ horizontal: "right", vertical: "top" }}
            targetOrigin={{ horizontal: "right", vertical: "top" }}
        >
            {menuItems.map((item, index) => {
                if (item.subItems && item.subItems.length > 0) {
                    return (
                        <MenuItem
                            key={`${item.primaryText}-${index}`}
                            data-test={item["data-test"]}
                            data-view={item["data-view"]}
                            data-active={item["data-active"]}
                            data-status={item["data-status"]}
                            primaryText={
                                <div {...nestedStyles}>
                                    {item.primaryText}
                                    <ChevronRight />
                                </div>
                            }
                            onClick={item.onClick}
                            menuItems={item.subItems.map((subItem, subIndex) => {
                                if (subItem.subItems && subItem.subItems.length > 0) {
                                    return (
                                        <MenuItem
                                            key={index}
                                            data-test={subItem["data-test"]}
                                            data-view={subItem["data-view"]}
                                            data-active={subItem["data-active"]}
                                            data-status={subItem["data-status"]}
                                            primaryText={
                                                <div {...nestedStyles}>
                                                    {subItem.primaryText}
                                                    <ChevronRight />
                                                </div>
                                            }
                                            onClick={subItem.onClick}
                                            menuItems={subItem.subItems.map((subSubItems, subSubIndex) => {
                                                return (
                                                    <MenuItem
                                                        key={`${subSubItems.primaryText}-${subSubIndex}`}
                                                        data-test={subSubItems["data-test"]}
                                                        data-view={subSubItems["data-view"]}
                                                        data-active={subSubItems["data-active"]}
                                                        data-status={subSubItems["data-status"]}
                                                        primaryText={subSubItems.primaryText}
                                                        onClick={subSubItems.onClick}
                                                    />
                                                );
                                            })}
                                        />
                                    );
                                }

                                return (
                                    <MenuItem
                                        key={`${subItem.primaryText}-${subIndex}`}
                                        data-test={subItem["data-test"]}
                                        data-view={subItem["data-view"]}
                                        data-active={subItem["data-active"]}
                                        data-status={subItem["data-status"]}
                                        primaryText={subItem.primaryText}
                                        onClick={subItem.onClick}
                                    />
                                );
                            })}
                        />
                    );
                }

                return (
                    <MenuItem
                        key={`${item.primaryText}-${index}`}
                        data-test={item["data-test"]}
                        data-view={item["data-view"]}
                        data-active={item["data-active"]}
                        data-status={item["data-status"]}
                        primaryText={item.primaryText}
                        onClick={item.onClick}
                    />
                );
            })}
        </Menu>
    );
};

export default memo(MenuActions);
